import cloneDeep from 'lodash/cloneDeep';
import { useRef } from 'react';
import { getTrackFunc, IExportLayerOriginInfo, useFramework, useIdentity } from '@/framework';
import type { ITouchKitEvent } from '@/utils/event-kit';
import { translatePointAngle } from '@/utils/math-kit';
import { asyncTraverseTree } from '@/utils/tree-kit';
import { BaseLayerViewType, DirectionType, ResizeOperateType, type ILayerProps } from '@/version-types';
import { MouseOperate } from '../../mouse-operate';
import { SnapCandidateResolver, type ResizeSnapSlot } from '../../operation-events';
import { useStickyInfo } from './use-sticky-info';

export const useResizeOperate = () => {
    const framework = useFramework();
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<IExportLayerOriginInfo>();
    const mouseOperate = framework.service.registration.getInstance<MouseOperate>('MouseOperate');
    const sticky = useStickyInfo();

    return {
        beforeResize(layerId: string, _operateType: ResizeOperateType) {
            const layer = framework.service.flatLayer.getLayer(layerId, true);
            oldInfo.current = layer;
            originLayerInfo.current = framework.service.frame.exportLayerOriginInfo(layerId);
            framework.service.frame.__emitBeforeBaseOperate(layer, BaseLayerViewType.RESIZE);
            sticky.before();
        },

        /**
         * 缩放
         *
         * @param id - 图层 ID
         * @param rotate - 旋转角度
         * @param operateType - 操作类型
         * @param e - 触摸事件对象
         */
        resize(id: string, rotate: number, operateType: ResizeOperateType, e: ITouchKitEvent) {
            if (!oldInfo.current || !originLayerInfo.current) return;
            const resizeLayer = framework.service.flatLayer.getLayer(id);
            if (!resizeLayer) {
                mouseOperate.emitCancel();
                return;
            }

            const isChangeHeight = operateType === ResizeOperateType.TC || operateType === ResizeOperateType.BC;

            // 获取操作方向
            const direction =
                operateType === ResizeOperateType.LC || operateType === ResizeOperateType.TC
                    ? DirectionType.OPPOSITE
                    : DirectionType.POSITIVE;

            let reverse = operateType === ResizeOperateType.LC;
            if (isChangeHeight) {
                reverse = operateType === ResizeOperateType.TC;
            }

            if (e.isStart) {
                framework.service.editor.setIsBaseOperate(true);
                framework.service.frame.addOrUpdateLayer({
                    ...cloneDeep(resizeLayer),
                    __isBasicOperating: true,
                });
                mouseOperate.emitResizeStart({
                    layerId: id,
                    oldLayer: oldInfo.current,
                    layer: mouseOperate.resolveCurrentOperateLayer(),
                    operateType,
                    direction,
                    reverse,
                    isChangeHeight,
                    pointerX: e.x,
                    pointerY: e.y,
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                });
                return;
            }

            if (e.isEnd) {
                framework.service.editor.setIsBaseOperate(false);
                const latestLayer = framework.service.flatLayer.getLayer(id);
                if (latestLayer) {
                    latestLayer.__isBasicOperating = undefined;
                }
            }
            const scale = framework.service.edgelessCanvas.zoom;

            const startPoint = translatePointAngle(e.startPoint, -rotate);
            const currentPoint = translatePointAngle(e.currentPoint, -rotate);

            const rawDisplacementX = !isChangeHeight ? Math.floor((currentPoint.x - startPoint.x) / scale) : 0;
            const rawDisplacementY = isChangeHeight ? Math.floor((currentPoint.y - startPoint.y) / scale) : 0;
            const rawOffset = (isChangeHeight ? rawDisplacementY : rawDisplacementX) * direction;
            const candidates = new SnapCandidateResolver<ResizeSnapSlot>({
                'resize-offset': rawOffset,
            });
            mouseOperate.emitResizeBeforeUpdate({
                layerId: id,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                operateType,
                direction,
                reverse,
                isChangeHeight,
                rawOffset,
                rawDisplacementX,
                rawDisplacementY,
                candidates,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });
            const offset = candidates.get('resize-offset');
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;
            const { width, height, isMin } = framework.service.frame.updateLayerResize(
                id,
                offset,
                direction,
                reverse,
                isChangeHeight,
                originInfo!,
                childOriginInfoMap,
                { isStart: e.isStart, isEnd: e.isEnd },
            );
            mouseOperate.emitResizeAfterUpdate({
                layerId: id,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                operateType,
                direction,
                reverse,
                isChangeHeight,
                appliedOffset: offset,
                width,
                height,
                isMin,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });

            sticky.pending(id);

            if (e.isEnd) {
                sticky.clear();
                // console.log('------------------');
                mouseOperate.emitResizeEnd({
                    layerId: id,
                    oldLayer: oldInfo.current,
                    layer: mouseOperate.resolveCurrentOperateLayer(),
                    operateType,
                    direction,
                    reverse,
                    isChangeHeight,
                    appliedOffset: offset,
                    width,
                    height,
                    isMin,
                    pointerX: e.x,
                    pointerY: e.y,
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                });
                const oldLayer = oldInfo.current;
                // let hasEffect = false;
                asyncTraverseTree(oldLayer, async (layer) => {
                    await framework.service.behavior.get(layer)?.onBaseResizeEnd?.(layer.id);
                    // const result = await framework.service.behavior.get(layer)?.onBaseResizeEnd?.(layer.id);
                    // if (result && !hasEffect) {
                    //     hasEffect = true;
                    // }
                }).then(() => {
                    // if (hasEffect) {
                    //     const layer = framework.service.flatLayer.getLayer(id, true);
                    //     if (layer) {
                    //         framework.service.frame.addOrUpdateLayer(layer);
                    //     }
                    // }
                    // if (!stickyInfo.current) {
                    // const layer = framework.service.flatLayer.getLayer(id, true);
                    // framework.service.frame.addOrUpdateLayer(layer, layer.__originIndex, true);

                    if (sticky.hasSticky()) {
                        sticky.after(oldLayer);
                    } else {
                        // console.log('普通调整尺寸');
                        // } else {
                        //     // 粘性替换
                        //     console.log('粘性缩放', stickyInfo.current);
                        // }
                        framework.service.history.write(oldLayer, getTrackFunc(identity, oldLayer, 'resize'));
                    }
                    framework.service.agentLayout.arrangeLayerFrame(id);
                });
                // framework.service.frame.addOrUpdateLayer(framework.service.flatLayer.getLayer(id, true), {
                //     skipEdgelessUpdate: true,
                // });
                framework.service.frame.setActiveLayer(id);
            }
        },
    };
};
